import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, Copy, Check, ListFilter } from 'lucide-react';
import { toast } from 'sonner';

interface EnumPopoverProps {
  values: string[];
  fieldName: string;
  children?: React.ReactNode;
}

export function EnumPopover({ values, fieldName, children }: EnumPopoverProps) {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const filteredValues = values.filter(v =>
    v.toLowerCase().includes(search.toLowerCase())
  );

  const handleCopyAll = () => {
    navigator.clipboard.writeText(values.join('\n'));
    setCopied(true);
    toast.success('Values copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Badge
            variant="outline"
            className="cursor-pointer hover:bg-muted transition-colors bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20"
          >
            <ListFilter className="h-3 w-3 mr-1" />
            {values.length} values
          </Badge>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3 border-b">
          <h4 className="font-medium text-sm mb-2">{fieldName}</h4>
          {values.length > 5 && (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter values..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-7 text-sm"
              />
            </div>
          )}
        </div>

        <ScrollArea className="max-h-48">
          <div className="p-2 space-y-1">
            {filteredValues.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-2">
                No matching values
              </p>
            ) : (
              filteredValues.map((value) => (
                <div
                  key={value}
                  className="px-2 py-1.5 rounded text-sm font-mono bg-muted/50 hover:bg-muted transition-colors"
                >
                  {value}
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <div className="p-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={handleCopyAll}
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 mr-1" />
                Copy All Values
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
